import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ShieldCheck,
  ShieldAlert,
  Building2,
  FileText,
  Phone,
  MapPin,
  Globe,
  CheckCircle2,
  AlertCircle,
  Clock,
  X,
  Send,
  Lock,
  Sparkles,
} from 'lucide-react';
import { api } from '../../../shared/api/api.js';
import { useAuthStore } from '../store/useAuthStore.js';
import { useNotificationStore } from '../../notifications/store/useNotificationStore.js';
import type { User } from '../../../shared/types/index.js';

interface PublisherAccountVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const PublisherAccountVerificationModal: React.FC<PublisherAccountVerificationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { user, updateUser } = useAuthStore();
  const { showToast } = useNotificationStore();

  const [legalName, setLegalName] = useState(user?.publisherLegalName || user?.organization || user?.name || '');
  const [nib, setNib] = useState(user?.publisherNib || '');
  const [phone, setPhone] = useState(user?.publisherPhone || '');
  const [address, setAddress] = useState(user?.publisherAddress || '');
  const [website, setWebsite] = useState(user?.publisherWebsite || '');
  const [agreeTerms, setAgreeTerms] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (!isOpen || !user) return null;

  const isOrg = user.role === 'organisasi';
  const entityLabel = isOrg ? 'Organisasi / Komunitas' : 'Lembaga Penerbit';

  const isVerified = Boolean(
    user.isPublisherVerified === 1 ||
    user.isPublisherVerified === true ||
    user.publisherVerificationStatus === 'verified'
  );
  const isPending = user.publisherVerificationStatus === 'pending';
  const isRejected = user.publisherVerificationStatus === 'rejected';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (!legalName.trim()) {
      setErrorMsg(`Nama resmi ${entityLabel.toLowerCase()} wajib diisi.`);
      return;
    }
    if (!phone.trim()) {
      setErrorMsg('Nomor telepon / WhatsApp resmi wajib diisi.');
      return;
    }
    if (!address.trim()) {
      setErrorMsg('Alamat lengkap kantor operasional / sekretariat wajib diisi.');
      return;
    }
    if (!agreeTerms) {
      setErrorMsg('Anda wajib menyetujui integritas dan kepatuhan standar penerbitan UIBN.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await api.submitPublisherVerification({
        legalName: legalName.trim(),
        nib: nib.trim() || undefined,
        phone: phone.trim(),
        address: address.trim(),
        website: website.trim() || undefined,
      });

      if (res.user) {
        updateUser(res.user);
      }

      showToast(
        isRejected ? 'Verifikasi Ulang Dikirim' : 'Verifikasi Diajukan',
        `Data profil dan legalitas ${entityLabel.toLowerCase()} berhasil dikirim ke tim admin untuk peninjauan.`,
        'success'
      );
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setErrorMsg(err.message || 'Gagal mengajukan verifikasi profil');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Instant Verification feature for quick testing / admin override
  const handleFastTrackVerify = async () => {
    setIsSubmitting(true);
    setErrorMsg(null);
    try {
      const res = await api.verifyPublisherAdmin(user.id, 'verified', 'Verifikasi instan mandiri');
      if (res.user) {
        updateUser(res.user);
      }
      showToast(
        'Penerbit Berhasil Diverifikasi',
        'Status akun Anda kini telah menjadi Penerbit Resmi Terverifikasi UIBN.',
        'success'
      );
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setErrorMsg(err.message || 'Gagal memproses verifikasi instan');
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-publisher-verify-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-xs animate-in fade-in duration-200"
    >
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 sm:px-6 sm:py-5 border-b border-slate-200 bg-gradient-to-r from-purple-950 via-purple-900 to-indigo-950 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 text-amber-400 shrink-0">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h2 id="modal-publisher-verify-title" className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                <span>
                  {isOrg
                    ? isRejected
                      ? 'Perbaikan & Pengajuan Ulang Verifikasi Organisasi/Komunitas'
                      : 'Verifikasi Profil Organisasi / Komunitas'
                    : isRejected
                    ? 'Perbaikan & Pengajuan Ulang Verifikasi Penerbit'
                    : 'Verifikasi Legalitas Penerbit UIBN'}
                </span>
                {isVerified ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-400 text-slate-950 font-bold uppercase tracking-wider flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3 text-slate-950" />
                    Terverifikasi
                  </span>
                ) : isPending ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-400 text-slate-950 font-bold uppercase tracking-wider flex items-center gap-1">
                    <Clock className="w-3 h-3 text-slate-950" />
                    Menunggu Verifikasi
                  </span>
                ) : isRejected ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-400 text-slate-950 font-bold uppercase tracking-wider flex items-center gap-1">
                    <ShieldAlert className="w-3 h-3 text-slate-950" />
                    Perlu Revisi
                  </span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-400 text-slate-950 font-bold uppercase tracking-wider flex items-center gap-1">
                    <ShieldAlert className="w-3 h-3 text-slate-950" />
                    Wajib Verifikasi
                  </span>
                )}
              </h2>
              <p className="text-xs text-purple-200/90 mt-0.5">
                {isRejected
                  ? 'Perbaiki isian data sesuai catatan penolakan admin di bawah untuk mengirim ulang verifikasi.'
                  : isOrg
                  ? 'Hanya organisasi/komunitas terverifikasi yang dapat mendaftarkan buku ber-UIBN ke Admin Pusat.'
                  : 'Hanya penerbit terverifikasi yang dapat mendaftarkan buku di UIBN dan menerima naskah dari penulis.'}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Tutup modal verifikasi penerbit"
            className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition cursor-pointer shrink-0"
          >
            <X className="w-4.5 h-4.5" />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <div className="p-5 sm:p-6 overflow-y-auto space-y-5 flex-1 text-slate-700">
          {/* Status Alert Banner */}
          {isVerified ? (
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-950 flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="text-xs">
                <span className="font-bold text-emerald-900 block text-sm mb-0.5">
                  Akun {entityLabel} Terverifikasi
                </span>
                {entityLabel} Anda telah terverifikasi secara sah di pangkalan data nasional UIBN (Kode: {user.publisherCode || 'Resmi'}). Anda dapat mendaftarkan buku baru secara langsung ke Admin UIBN.
              </div>
            </div>
          ) : isPending ? (
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-950 flex items-start gap-3">
              <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-xs">
                <span className="font-bold text-amber-900 block text-sm mb-0.5">
                  Permohonan Verifikasi Sedang Ditinjau Admin
                </span>
                Data legalitas {entityLabel.toLowerCase()} Anda telah berhasil dikirim. Tim kurator UIBN sedang memeriksa dokumen Anda (Proses verifikasi maksimal 1×24 jam). Anda dapat memperbarui isian data jika ada perubahan sebelum disetujui.
              </div>
            </div>
          ) : isRejected ? (
            <div className="p-4 rounded-xl bg-rose-50 border border-rose-300 text-rose-950 space-y-2">
              <div className="flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                <div className="text-xs flex-1">
                  <span className="font-bold text-rose-900 block text-sm mb-0.5">
                    Permohonan Verifikasi Memerlukan Revisi
                  </span>
                  Permohonan sebelumnya ditolak oleh admin karena berkas/data belum memenuhi syarat verifikasi UIBN.
                </div>
              </div>

              {user.publisherVerificationNotes && (
                <div className="bg-white/90 p-3 rounded-lg border border-rose-200 text-xs">
                  <span className="font-bold text-rose-900 block mb-1">
                    Catatan / Alasan Penolakan dari Admin UIBN:
                  </span>
                  <p className="text-rose-800 font-medium">
                    "{user.publisherVerificationNotes}"
                  </p>
                </div>
              )}

              <p className="text-[11px] text-rose-800 pt-1">
                Silakan tinjau dan perbaiki data yang belum sesuai pada formulir di bawah, lalu klik <strong>"Kirim Ulang Verifikasi"</strong>.
              </p>
            </div>
          ) : (
            <div className="p-4 rounded-xl bg-purple-50 border border-purple-200 text-purple-950 flex items-start gap-3">
              <Building2 className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
              <div className="text-xs">
                <span className="font-bold text-purple-900 block text-sm mb-0.5">
                  Verifikasi Pertama Kali Diperlukan
                </span>
                Sebagai penerbit yang baru mendaftar, Anda wajib melengkapi verifikasi identitas lembaga penerbit sebelum dapat melakukan pendaftaran buku atau mencetak barcode/e-sertifikat UIBN.
              </div>
            </div>
          )}

          {errorMsg && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Nama Legal */}
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-bold text-slate-700">
                    {isOrg ? 'Nama Resmi Organisasi / Komunitas' : 'Nama Resmi Badan Usaha / Penerbit'}{' '}
                    <span className="text-rose-500">*</span>
                  </label>
                  {isVerified && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md">
                      <Lock className="w-3 h-3 text-amber-600" />
                      <span>Terkunci (Terverifikasi)</span>
                    </span>
                  )}
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                    {isVerified ? <Lock className="w-4 h-4 text-amber-600" /> : <Building2 className="w-4 h-4" />}
                  </div>
                  <input
                    type="text"
                    required
                    readOnly={isVerified}
                    value={legalName}
                    onChange={(e) => !isVerified && setLegalName(e.target.value)}
                    placeholder={
                      isOrg
                        ? 'Contoh: Komunitas Sastra Mahasiswa / Yayasan Literasi Bangsa'
                        : 'Contoh: PT Lentera Pustaka Indonesia / Yayasan Literasi Bangsa'
                    }
                    className={`w-full pl-9 pr-3.5 py-2 text-xs sm:text-sm border rounded-xl transition ${
                      isVerified
                        ? 'bg-slate-100 text-slate-800 border-slate-200 font-semibold cursor-not-allowed select-none'
                        : 'border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-purple-500/30 focus:border-purple-600 bg-white'
                    }`}
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  Nama penerbit & organisasi/komunitas yang didaftarkan akan dikunci dan tidak bisa diubah setelah diverifikasi dan disetujui oleh admin.
                </p>
              </div>

              {/* NIB / SK Notaris */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Nomor Induk Berusaha (NIB) / SK Notaris
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                    <FileText className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    value={nib}
                    onChange={(e) => setNib(e.target.value)}
                    placeholder="Contoh: 0220001234567 atau AHU-00123"
                    className="w-full pl-9 pr-3.5 py-2 text-xs sm:text-sm border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-purple-500/30 focus:border-purple-600 bg-white font-mono"
                  />
                </div>
              </div>

              {/* No WhatsApp / Telepon */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  No. Telepon / WhatsApp Resmi Redaksi <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                    <Phone className="w-4 h-4" />
                  </div>
                  <input
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Contoh: 081234567890 atau 021-5551234"
                    className="w-full pl-9 pr-3.5 py-2 text-xs sm:text-sm border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-purple-500/30 focus:border-purple-600 bg-white"
                  />
                </div>
              </div>

              {/* Alamat Kantor */}
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Alamat Lengkap Kantor Operasional <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute top-2.5 left-3 pointer-events-none text-slate-400">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <textarea
                    rows={2}
                    required
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Contoh: Gedung Graha Literasi Lt. 3, Jl. Merdeka No. 45, Jakarta Pusat"
                    className="w-full pl-9 pr-3.5 py-2 text-xs sm:text-sm border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-purple-500/30 focus:border-purple-600 bg-white"
                  />
                </div>
              </div>

              {/* Website / Media Sosial */}
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Situs Web / Media Sosial Resmi (Opsional)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                    <Globe className="w-4 h-4" />
                  </div>
                  <input
                    type="url"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://penerbit-anda.com atau https://instagram.com/penerbitanda"
                    className="w-full pl-9 pr-3.5 py-2 text-xs sm:text-sm border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-purple-500/30 focus:border-purple-600 bg-white font-mono"
                  />
                </div>
              </div>
            </div>

            {/* Checkbox Pernyataan */}
            <div className="p-3.5 rounded-xl bg-purple-50/50 border border-purple-200/80 space-y-2">
              <label className="flex items-start gap-2.5 cursor-pointer text-xs text-purple-950 font-medium">
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  className="w-4 h-4 text-purple-600 rounded-md focus:ring-purple-500 mt-0.5 shrink-0"
                />
                <span>
                  Saya menyatakan bahwa data identitas lembaga penerbit di atas adalah benar dan sah. Penerbit bersedia mematuhi seluruh kode etik perbukuan nasional dan regulasi nomor unik UIBN.
                </span>
              </label>
            </div>

            {/* Action Buttons */}
            <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-2.5">
              <button
                type="button"
                onClick={handleFastTrackVerify}
                disabled={isSubmitting}
                className="w-full sm:w-auto px-4 py-2 text-xs font-bold bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-xl border border-amber-300 transition flex items-center justify-center gap-1.5 cursor-pointer"
                title="Verifikasi langsung untuk uji coba cepat sistem"
              >
                <Sparkles className="w-3.5 h-3.5 text-amber-700" />
                <span>Verifikasi Otomatis (Demo/Uji Coba)</span>
              </button>

              <div className="flex items-center gap-2 w-full sm:w-auto ml-auto">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 sm:flex-none px-4 py-2 text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition cursor-pointer"
                >
                  Tutup
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting || !agreeTerms}
                  className="flex-1 sm:flex-none px-5 py-2 text-xs font-bold bg-purple-700 hover:bg-purple-800 disabled:opacity-50 text-white rounded-xl shadow-xs transition flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>
                    {isSubmitting
                      ? 'Mengirim...'
                      : isRejected
                      ? 'Kirim Ulang Verifikasi'
                      : isPending
                      ? 'Perbarui Data Pengajuan'
                      : 'Ajukan Verifikasi'}
                  </span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};
